import { ResizeNotifier } from '@theme/critical';
import { prefersReducedMotion } from '@theme/utilities';

/**
 * <jumbo-text>
 * Auto-resizes text to fit container width.
 * SAFE for HERO – text never disappears
 */
class JumboText extends HTMLElement {
  connectedCallback() {
    if (!this.textContent?.trim()) return;

    // IMPORTANT: ensure visible from start
    this.classList.add('ready');

    // Initial run after first paint
    requestAnimationFrame(() => this.#handleResize());

    // React to viewport resize
    this._onWindowResize = () => {
      requestAnimationFrame(() => this.#handleResize());
    };
    window.addEventListener('resize', this._onWindowResize);

    if (
      this.dataset.textEffect &&
      this.dataset.textEffect !== 'none' &&
      !prefersReducedMotion()
    ) {
      this.#setIntersectionObserver();
    }
  }

  disconnectedCallback() {
    this.#resizeObserver.disconnect();
    this.intersectionObserver?.disconnect();
    window.removeEventListener('resize', this._onWindowResize);
  }

  #setIntersectionObserver() {
    this.intersectionObserver = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            this.classList.add('jumbo-text-visible');

            if (this.dataset.animationRepeat === 'false') {
              this.intersectionObserver.unobserve(entry.target);
            }
          } else {
            this.classList.remove('jumbo-text-visible');
          }
        }
      },
      { threshold: 0.3 }
    );

    this.intersectionObserver.observe(this);
  }

  #handleResize = () => {
    /* READS */
    const containerWidth = this.clientWidth;
    if (containerWidth <= 0) return;

    const rect = this.getBoundingClientRect();
    const pageHeight = document.documentElement.offsetHeight;
    const bottom = rect.bottom + window.scrollY;
    const distanceFromBottom = pageHeight - bottom;

    /* COMPUTE */
    const fontSize = findOptimalFontSize(this, containerWidth);

    /* WRITE (SAFE) */
    if (fontSize && fontSize > 0) {
      this.style.fontSize = `${fontSize}px`;
    }

    this.dataset.capText = (distanceFromBottom <= 100).toString();
  };

  #resizeObserver = new ResizeNotifier(() => {
    requestAnimationFrame(() => this.#handleResize());
  });
}

/**
 * Checks if text overflows container at a given font size
 * (NO visibility hacks)
 */
function checkTextOverflow(element, containerWidth, size) {
  element.style.fontSize = `${size}px`;
  return element.scrollWidth > containerWidth;
}

/**
 * Binary search for optimal font size
 */
function findOptimalFontSize(element, containerWidth) {
  let minSize = 10; // IMPORTANT: never go below readable size
  let maxSize = 500;
  const precision = 0.5;
  const MAX_ITERATIONS = 30;

  const textLength = element.textContent?.length || 1;
  let fontSize = Math.min(
    maxSize,
    Math.sqrt(containerWidth) * (15 / Math.sqrt(textLength))
  );

  if (checkTextOverflow(element, containerWidth, fontSize)) {
    maxSize = fontSize;
  } else {
    minSize = fontSize;
  }

  let iterations = 0;

  while (maxSize - minSize > precision && iterations < MAX_ITERATIONS) {
    fontSize = (minSize + maxSize) / 2;

    if (checkTextOverflow(element, containerWidth, fontSize)) {
      maxSize = fontSize;
    } else {
      minSize = fontSize;
    }

    iterations++;
  }

  return minSize * 0.99;
}

if (!customElements.get('jumbo-text')) {
  customElements.define('jumbo-text', JumboText);
}
